// MyRedPark API client — cookie-based session (httpOnly `mrp_session`).
// No auth token is ever stored in JS-accessible storage; the backend maps the
// cookie to a Bearer token via middleware. All calls go to the SAME FastAPI
// backend the mobile app uses.
const BASE = process.env.REACT_APP_BACKEND_URL;

export class ApiError extends Error {
  constructor(status, detail, code = null) {
    super(detail || `Request failed (${status})`);
    this.status = status;
    this.code = code;
  }
}

async function req(path, { method = "GET", body } = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "Content-Type": "application/json" } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, "Network error — check your connection and retry.");
  }
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!res.ok) {
    const detail = data && (data.detail || data.message);
    // Server gate errors carry a structured detail ({code, message}) — surface both.
    const code = detail && typeof detail === "object" ? detail.code : null;
    const msg = typeof detail === "string" ? detail
      : (detail && detail.message) || "Something went wrong. Please try again.";
    if (code === "TERMS_REQUIRED") window.dispatchEvent(new CustomEvent("mrp:terms-required"));
    const err = new ApiError(res.status, msg, code);
    if (detail && typeof detail === "object") err.detail = detail; // structured payloads (e.g. deletion blockers)
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  register: (b) => req("/api/auth/register", { method: "POST", body: b }),
  login: (b) => req("/api/auth/login", { method: "POST", body: b }),
  googleAuth: (credential) => req("/api/auth/google", { method: "POST", body: { credential } }),
  me: () => req("/api/auth/me"),
  logout: () => req("/api/auth/logout", { method: "POST", body: {} }),
  setMode: (mode) => req("/api/auth/mode", { method: "POST", body: { mode } }),
  changePassword: (b) => req("/api/auth/password/change", { method: "POST", body: b }),
  verifyEmailRequest: () => req("/api/auth/email/verify-request", { method: "POST", body: {} }),
  verifyEmail: (token) => req("/api/auth/email/verify", { method: "POST", body: { token } }),
  deleteAccount: (b) => req("/api/auth/delete", { method: "POST", body: b || {} }),
  resetRequest: (email) => req("/api/auth/password/reset-request", { method: "POST", body: { email } }),
  resetConfirm: (token, password) => req("/api/auth/password/reset-confirm", { method: "POST", body: { token, password } }),

  // Pricing quote (server-authoritative — same engine as the PaymentIntent)
  pricingQuote: (spotId, start, end, applyPromo = false, pricingMode = null) => req(`/api/pricing/quote?spot_id=${spotId}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${applyPromo ? "&apply_promo=true" : ""}${pricingMode ? `&pricing_mode=${pricingMode}` : ""}`),
  // Versioned declarations & driver consent (server-authoritative)
  driverConsentStatus: () => req("/api/consent/driver"),
  driverConsentAccept: () => req("/api/consent/driver/accept", { method: "POST", body: { context: "web" } }),
  hostDeclaration: () => req("/api/declarations/host"),
  publicConfig: () => req("/api/config/public"),
  rewardsMe: () => req("/api/rewards/me"),
  spinReveal: (presented = true) => req("/api/promotions/spin/reveal", { method: "POST", body: { presented } }),
  referralsTrack: (code) => req("/api/referrals/track", { method: "POST", body: { code } }),
  adminPromotions: (qs = "") => req(`/api/admin/promotions${qs}`),
  adminPromotionMetrics: () => req("/api/admin/promotions/metrics"),
  adminCreatePromotion: (b) => req("/api/admin/promotions", { method: "POST", body: b }),
  adminUpdatePromotion: (id, b) => req(`/api/admin/promotions/${id}`, { method: "PATCH", body: b }),
  validateCoupon: (b) => req("/api/promotions/validate", { method: "POST", body: b }),

  // V2 — Terms gate (server-authoritative)
  termsStatus: () => req("/api/terms/status"),
  termsAccept: (context = "web") => req("/api/terms/accept", { method: "POST", body: { context } }),

  // V2 — Commercial mode
  commercialAccessStatus: () => req("/api/commercial/access-status"),
  commercialRequestAccess: (b = {}) => req("/api/commercial/access-request", { method: "POST", body: b }),
  commercialDashboard: () => req("/api/commercial/dashboard"),
  commercialProperties: () => req("/api/commercial/properties"),
  commercialPropertyCreate: (b) => req("/api/commercial/properties", { method: "POST", body: b }),
  commercialProperty: (id) => req(`/api/commercial/properties/${id}`),
  commercialPropertyPatch: (id, b) => req(`/api/commercial/properties/${id}`, { method: "PATCH", body: b }),
  commercialPropertyDrafts: () => req("/api/commercial/property-drafts"),
  commercialPropertyDraft: (id) => req(`/api/commercial/property-drafts/${id}`),
  commercialPropertyDraftSave: (id, b) => req(`/api/commercial/property-drafts/${id}`, { method: "PUT", body: b }),
  commercialPropertyDraftDelete: (id) => req(`/api/commercial/property-drafts/${id}`, { method: "DELETE" }),
  commercialPropertyPublish: (id) => req(`/api/commercial/properties/${id}/publish`, { method: "POST", body: {} }),
  commercialPropertyUnpublish: (id) => req(`/api/commercial/properties/${id}/unpublish`, { method: "POST", body: {} }),
  commercialCategoryCreate: (id, b) => req(`/api/commercial/properties/${id}/categories`, { method: "POST", body: b }),
  commercialCategoryPatch: (id, cid, b) => req(`/api/commercial/properties/${id}/categories/${cid}`, { method: "PATCH", body: b }),
  commercialRateCreate: (id, cid, b) => req(`/api/commercial/properties/${id}/categories/${cid}/rates`, { method: "POST", body: b }),
  commercialRatePatch: (id, rid, b) => req(`/api/commercial/properties/${id}/rates/${rid}`, { method: "PATCH", body: b }),
  commercialSpots: (id, qs = "") => req(`/api/commercial/properties/${id}/spots${qs}`),
  adminCommercialRequests: () => req("/api/admin/commercial/requests"),
  adminCommercialAccess: (uid, action) => req(`/api/admin/commercial/access/${uid}`, { method: "POST", body: { action } }),
  adminCommercialOverview: () => req("/api/admin/commercial/overview"),
  adminCommercialProperties: (p = {}) => req(`/api/admin/commercial/properties${Object.keys(p).length ? `?${new URLSearchParams(p)}` : ""}`),
  adminCommercialPropertyStatus: (id, action) => req(`/api/admin/commercial/properties/${id}/status`, { method: "POST", body: { action } }),
  adminCommercialBookings: (p = {}) => req(`/api/admin/commercial/bookings${Object.keys(p).length ? `?${new URLSearchParams(p)}` : ""}`),
  commercialMonthlyReport: (month, propertyId) => req(`/api/commercial/reports/monthly?month=${month}${propertyId ? `&property_id=${propertyId}` : ""}`),

  // V2 — public property resolution (code / QR / deep link)
  publicProperty: (code, cat) => req(`/api/p/${code}${cat ? `/${cat}` : ""}`),

  // V2 P2 — commercial booking core (server-authoritative quote → hold → OTP/capture)
  commercialQuote: (p) => req(`/api/commercial/quote?${new URLSearchParams(p)}`),
  commercialAvailability: (p) => req(`/api/commercial/availability?${new URLSearchParams(p)}`),
  commercialBookingCreate: (b) => req("/api/commercial/bookings", { method: "POST", body: b }),
  commercialBookings: (p = {}) => req(`/api/commercial/bookings${Object.keys(p).length ? `?${new URLSearchParams(p)}` : ""}`),

  // V2 P2 Slice B — discounts / announcements / auto-renew
  commercialDiscountCreate: (id, b) => req(`/api/commercial/properties/${id}/discounts`, { method: "POST", body: b }),
  commercialDiscounts: (id) => req(`/api/commercial/properties/${id}/discounts`),
  commercialDiscountPatch: (id, pid2, action) => req(`/api/commercial/properties/${id}/discounts/${pid2}`, { method: "PATCH", body: { action } }),
  commercialAnnouncementCreate: (id, b) => req(`/api/commercial/properties/${id}/announcements`, { method: "POST", body: b }),
  commercialAnnouncements: (id) => req(`/api/commercial/properties/${id}/announcements`),
  commercialAnnouncementPatch: (id, aid, action) => req(`/api/commercial/properties/${id}/announcements/${aid}`, { method: "PATCH", body: { action } }),
  commercialSubscribe: (b) => req("/api/commercial/subscribe", { method: "POST", body: b }),

  // Invoices (immutable financial records)
  myInvoices: (page = 1) => req(`/api/invoices?page=${page}&page_size=25`),
  hostInvoices: (page = 1) => req(`/api/invoices/host?page=${page}&page_size=25`),
  getInvoice: (id) => req(`/api/invoices/${id}`),

  // Spots
  uploadListingPhoto: (file, onProgress) => new Promise((resolve, reject) => {
    // XHR (not fetch) for real upload progress events; cookie session via withCredentials.
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/api/spots/uploads`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON error body */ }
      if (xhr.status >= 200 && xhr.status < 300 && data) resolve(data);
      else reject(new ApiError(xhr.status, data?.detail || "Upload failed — please try again."));
    };
    xhr.onerror = () => reject(new ApiError(0, "Network error — check your connection and retry."));
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  }),
  nearby: (lat, lng, radius_km = 25) => req(`/api/spots/nearby?lat=${lat}&lng=${lng}&radius_km=${radius_km}`),
  getSpot: (id, code) => req(`/api/spots/${id}${code ? `?code=${encodeURIComponent(code)}` : ""}`),
  mySpots: () => req("/api/spots/host/mine"),
  createSpot: (b) => req("/api/spots", { method: "POST", body: b }),
  updateSpot: (id, b) => req(`/api/spots/${id}`, { method: "PATCH", body: b }),
  deleteSpot: (id) => req(`/api/spots/${id}`, { method: "DELETE" }),
  spotEarnings: (id) => req(`/api/spots/${id}/earnings`),
  spotBookings: (id) => req(`/api/spots/${id}/bookings`),
  spotAvailability: (id, start, end) => req(`/api/spots/${id}/availability?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
  spotReviews: (id) => req(`/api/spots/${id}/reviews`),

  // Geo
  geoAutocomplete: (q, lat, lng, country) => req(`/api/geo/autocomplete?q=${encodeURIComponent(q)}${lat != null ? `&lat=${lat}&lng=${lng}` : ""}${country ? `&country=${encodeURIComponent(country)}` : ""}`),
  geoReverse: (lat, lng) => req(`/api/geo/reverse?lat=${lat}&lng=${lng}`),
  geoTimezone: (lat, lng) => req(`/api/geo/timezone?lat=${lat}&lng=${lng}`),
  // Visitor browsing region (country) — server-detected from the request IP,
  // or a deliberate "Change region" choice stored as a session cookie.
  geoRegion: () => req("/api/geo/region"),
  setRegionPreference: (country) => req("/api/geo/region", { method: "POST", body: { country } }),
  clearRegionPreference: () => req("/api/geo/region", { method: "DELETE" }),
  // V2 correction pass: host custom fields, vehicle change, host overview
  propertyFields: (pid) => req(`/api/commercial/properties/${pid}/fields`),
  propertyFieldCreate: (pid, body) => req(`/api/commercial/properties/${pid}/fields`, { method: "POST", body }),
  propertyFieldPatch: (pid, fid, body) => req(`/api/commercial/properties/${pid}/fields/${fid}`, { method: "PATCH", body }),
  propertyFieldDelete: (pid, fid) => req(`/api/commercial/properties/${pid}/fields/${fid}`, { method: "DELETE" }),
  publicPropertyFields: (code, category) => req(`/api/p/${code}/fields${category ? `?category=${encodeURIComponent(category)}` : ""}`),
  changeBookingVehicle: (bid, vehicleId) => req(`/api/bookings/${bid}/vehicle`, { method: "POST", body: { vehicle_id: vehicleId } }),
  commercialVerification: () => req("/api/commercial/verification"),
  commercialVerificationStart: (b) => req("/api/commercial/verification/start", { method: "POST", body: b }),
  commercialVerificationConfirm: (code) => req("/api/commercial/verification/confirm", { method: "POST", body: { code } }),
  adminCommercialVerifications: (status) => req(`/api/admin/commercial/verifications${status ? `?status=${status}` : ""}`),
  adminCommercialVerificationReview: (uid, action, note) => req(`/api/admin/commercial/verifications/${uid}/review`, { method: "POST", body: { action, note } }),
  hostOverview: () => req("/api/host/overview"),
  getBookingInvoice: (bid) => req(`/api/bookings/${bid}/invoice`),

  // Bookings
  createBooking: (b) => req("/api/bookings", { method: "POST", body: b }),
  extendBooking: (id, minutes) => req(`/api/bookings/${id}/extend`, { method: "POST", body: { minutes } }),
  confirmExtension: (id, extId) => req(`/api/bookings/${id}/extensions/${extId}/confirm`, { method: "POST", body: {} }),
  getNotifPrefs: () => req("/api/notifications/preferences"),
  updateNotifPrefs: (b) => req("/api/notifications/preferences", { method: "PATCH", body: b }),
  myVehicles: () => req("/api/vehicles"),
  addVehicle: (b) => req("/api/vehicles", { method: "POST", body: b }),
  updateVehicle: (id, b) => req(`/api/vehicles/${id}`, { method: "PATCH", body: b }),
  deleteVehicle: (id) => req(`/api/vehicles/${id}`, { method: "DELETE" }),
  defaultVehicle: (id) => req(`/api/vehicles/${id}/default`, { method: "POST", body: {} }),
  myBookings: () => req("/api/bookings/mine"),
  hostBookings: () => req("/api/bookings/host"),
  getBooking: (id) => req(`/api/bookings/${id}`),
  getOtp: (id) => req(`/api/bookings/${id}/otp`),
  verifyOtp: (id, otp, vehicleConfirmed = false) => req(`/api/bookings/${id}/verify-otp`, { method: "POST", body: { otp, vehicle_confirmed: vehicleConfirmed } }),
  reportVehicleMismatch: (id) => req(`/api/bookings/${id}/vehicle-mismatch`, { method: "POST" }),
  retryCapture: (id) => req(`/api/bookings/${id}/retry-capture`, { method: "POST", body: {} }),
  reportIssue: (id, reason, details) => req(`/api/bookings/${id}/report-issue`, { method: "POST", body: { reason, details } }),
  bookingReview: (id) => req(`/api/bookings/${id}/review`),
  createReview: (b) => req("/api/reviews", { method: "POST", body: b }),
  cancelBooking: (id) => req(`/api/bookings/${id}/cancel`, { method: "POST", body: {} }),
  cancelPreview: (id) => req(`/api/bookings/${id}/cancel-preview`),
  updateBookingPricing: (id, pricing_mode) => req(`/api/bookings/${id}/pricing`, { method: "POST", body: { pricing_mode } }),
  personaConfig: () => req("/api/persona/config"),
  personaStatus: () => req("/api/persona/status"),
  personaBind: (inquiry_id) => req("/api/persona/inquiry", { method: "POST", body: { inquiry_id } }),
  adminPlatformSettings: () => req("/api/admin/platform-settings"),
  adminPersonaVerificationSet: (enabled) => req("/api/admin/platform-settings/persona-verification", { method: "PUT", body: { enabled } }),
  adminAccountDeletions: (qs_ = "") => req(`/api/admin/account-deletions${qs_}`),
  bookingChat: (id) => req(`/api/chats/${id}`),
  sendBookingMessage: (id, text) => req(`/api/chats/${id}/messages`, { method: "POST", body: { text } }),

  // Payments / Stripe
  checkoutWithOtp: (booking_id) => req("/api/stripe/checkout-with-otp", { method: "POST", body: { booking_id } }),
  connectOnboarding: (country, returnPath, businessType) => req("/api/stripe/connect/onboarding-link", { method: "POST", body: { country, ...(returnPath ? { return_path: returnPath } : {}), ...(businessType ? { business_type: businessType } : {}) } }),
  connectStatus: () => req("/api/stripe/connect/status"),
  subscribe: (spot_id) => req("/api/stripe/subscribe", { method: "POST", body: { spot_id } }),
  mySubscriptions: () => req("/api/subscriptions/mine"),
  cancelSubscription: (id) => req(`/api/stripe/cancel-subscription/${id}`, { method: "POST", body: {} }),

  // Wallet
  wallet: () => req("/api/wallet"),
  walletTransactions: (filter = "all") => req(`/api/wallet/transactions?filter=${filter}`),
  walletTopup: (amount_cents) => req("/api/wallet/topup", { method: "POST", body: { amount_cents } }),
  walletPayBooking: (booking_id) => req("/api/wallet/pay-booking", { method: "POST", body: { booking_id } }),
  cashoutInstant: () => req("/api/wallet/cashout/instant", { method: "POST", body: {} }),
  cashoutSettings: (b) => req("/api/wallet/cashout/settings", { method: "POST", body: b }),

  // Dashboards
  driverDashboard: () => req("/api/dashboard/driver"),
  hostDashboard: () => req("/api/dashboard/host"),

  // Notifications
  notifications: () => req("/api/notifications"),
  unreadCount: () => req("/api/notifications/unread_count"),
  markRead: (id) => req(`/api/notifications/${id}/read`, { method: "POST", body: {} }),
  markAllRead: () => req("/api/notifications/read_all", { method: "POST", body: {} }),

  // Support (user)
  aiChat: (message, conversation_id) => req("/api/ai/chat", { method: "POST", body: { message, conversation_id } }),
  aiConversations: () => req("/api/ai/conversations"),
  aiEscalate: (conversation_id, reason) => req("/api/ai/escalate", { method: "POST", body: { conversation_id, reason } }),
  createTicket: (b) => req("/api/support/tickets", { method: "POST", body: b }),
  myTickets: () => req("/api/support/tickets"),
  getTicket: (id) => req(`/api/support/tickets/${id}`),
  replyTicket: (id, text) => req(`/api/support/tickets/${id}/messages`, { method: "POST", body: { text } }),

  // Support console (support/admin)
  supportDashboard: () => req("/api/support/dashboard"),
  supportAllTickets: (params = {}) => req(`/api/support/tickets/all/list${(() => { const p = new URLSearchParams(); Object.entries(params).forEach(([k, v]) => { if (v) p.set(k, v); }); const s = p.toString(); return s ? `?${s}` : ""; })()}`),
  supportSearchUsers: (q) => req(`/api/support/users/search?q=${encodeURIComponent(q)}`),
  patchTicket: (id, b) => req(`/api/support/tickets/${id}`, { method: "PATCH", body: b }),

  // Admin (all list endpoints are server-paginated envelopes: {items, total, page, page_size})
  adminStats: () => req("/api/admin/stats"),
  adminAnalytics: (qs = "") => req(`/api/admin/analytics${qs ? `?${qs}` : ""}`),
  adminReviews: (qs = "") => req(`/api/admin/reviews${qs}`),
  adminUsers: (qs = "") => req(`/api/admin/users${qs}`),
  adminHosts: (qs = "") => req(`/api/admin/hosts${qs}`),
  adminDrivers: (qs = "") => req(`/api/admin/drivers${qs}`),
  adminBookings: (qs = "") => req(`/api/admin/bookings${qs}`),
  adminPayments: (qs = "") => req(`/api/admin/payments${qs}`),
  adminTickets: (qs = "") => req(`/api/admin/tickets${qs}`),
  adminAudit: (qs = "") => req(`/api/admin/audit${qs}`),
  adminConsents: (type = null) => req(`/api/admin/consents${type ? `?type=${type}` : ""}`),
  adminSpots: (qs = "") => req(`/api/admin/spots${qs}`),
  adminPatchUser: (id, b) => req(`/api/admin/users/${id}`, { method: "PATCH", body: b }),
  adminDeleteUser: (id) => req(`/api/admin/users/${id}`, { method: "DELETE" }),
  adminPatchSpot: (id, b) => req(`/api/admin/spots/${id}`, { method: "PATCH", body: b }),
  adminDeleteSpot: (id) => req(`/api/admin/spots/${id}`, { method: "DELETE" }),
  adminRefund: (id, reason) => req(`/api/admin/bookings/${id}/refund`, { method: "POST", body: { reason } }),
  adminOverrideOtp: (id) => req(`/api/admin/bookings/${id}/override-otp`, { method: "POST", body: {} }),
  adminBroadcast: (b) => req("/api/admin/broadcast", { method: "POST", body: b }),

  // Trust & safety
  verifySpotLocation: (id, b) => req(`/api/spots/${id}/verify-location`, { method: "POST", body: b }),
  reportListing: (b) => req("/api/reports/listing", { method: "POST", body: b }),
  adminListingQueue: () => req("/api/admin/listings/queue"),
  adminReviewListing: (id, b) => req(`/api/admin/listings/${id}/review`, { method: "POST", body: b }),
  adminReports: (status = "open") => req(`/api/admin/reports?status=${status}`),
  adminResolveReport: (id, b) => req(`/api/admin/reports/${id}/resolve`, { method: "POST", body: b }),
  supportListingQueue: () => req("/api/support/listings/queue"),
  supportReports: (status = "open") => req(`/api/support/reports?status=${status}`),
  adminResolveTicket: (id) => req(`/api/admin/tickets/${id}/resolve`, { method: "POST", body: {} }),
  adminCommission: () => req("/api/admin/commission"),
  adminTaxStatus: () => req("/api/admin/tax-status"),
  adminPublishCommission: (b) => req("/api/admin/commission", { method: "POST", body: b }),

  // Legal
  terms: () => req("/api/terms"),
  privacy: () => req("/api/privacy"),
  commercialTerms: () => req("/api/commercial-terms"),

  // Property security / live operations (V2 P3)
  securitySharedProperties: () => req("/api/security/properties"),
  securityOverview: (pid) => req(`/api/security/properties/${pid}/overview`),
  securityReports: (pid) => req(`/api/security/properties/${pid}/reports`),
  securityCreateReport: (pid, b) => req(`/api/security/properties/${pid}/reports`, { method: "POST", body: b }),
  securityFields: (pid) => req(`/api/security/properties/${pid}/fields`),
  securityChat: (pid) => req(`/api/security/properties/${pid}/chat`),
  securityChatSend: (pid, text) => req(`/api/security/properties/${pid}/chat`, { method: "POST", body: { text } }),
  securityAccessList: (pid) => req(`/api/commercial/properties/${pid}/security/access`),
  securityAccessGrant: (pid, b) => req(`/api/commercial/properties/${pid}/security/access`, { method: "POST", body: b }),
  securityAccessRevoke: (pid, aid) => req(`/api/commercial/properties/${pid}/security/access/${aid}/revoke`, { method: "POST", body: {} }),
  securityFieldsOwner: (pid) => req(`/api/commercial/properties/${pid}/security/fields`),
  securityFieldCreate: (pid, b) => req(`/api/commercial/properties/${pid}/security/fields`, { method: "POST", body: b }),
  securityFieldPatch: (pid, fid, b) => req(`/api/commercial/properties/${pid}/security/fields/${fid}`, { method: "PATCH", body: b }),
  securityChatThreads: (pid) => req(`/api/commercial/properties/${pid}/security/chats`),
  securityChatOwnerRead: (pid, uid) => req(`/api/commercial/properties/${pid}/security/chats/${uid}/messages`),
  securityChatOwnerSend: (pid, uid, text) => req(`/api/commercial/properties/${pid}/security/chats/${uid}/messages`, { method: "POST", body: { text } }),
};
